import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import {
  fetchConnectStatus,
  startNewPayoutAccount,
  type ConnectStatus,
} from '@/lib/api';
import { ConnectStatusCard, connectCardState } from '@/components/connect/ConnectStatusCard';
import { PayoutCountryField } from '@/components/connect/PayoutCountryField';
import {
  apiTakesCountry, canStartPayout, isCountryRequiredCode, payoutCountryFrom, payoutCountryTextKey,
  readChooserFrom, type PayoutCountry,
} from '@/lib/payoutCountry';
import { useAuthStore } from '@/store/auth';
import formStyles from '@/components/Form.module.css';
import styles from './ConnectCompletePage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Where the PWA may resume after Stripe. Same shape the server
 * allowlists on POST /me/connect/start (CONNECT_NEXT_ALLOWED) — the
 * earn walk and nothing else — checked again here because the query
 * string is user-editable.
 */
const NEXT_ALLOWED = /^\/my-chargers\/[a-f0-9]{24}\/earn$/;
function readNext(): string | null {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && NEXT_ALLOWED.test(next) ? next : null;
}

/** The charger page or Earnings that opened this page as the chooser
 *  (payoutChooserPath), for the back button. Client only. */
function readFrom(): string | null {
  return readChooserFrom(window.location.search);
}

/**
 * Stripe-hosted onboarding redirects here on completion (or on
 * link refresh). Reads the live Connect status and gives the
 * resident an honest read on whether onboarding actually went
 * through, what's still missing, and how to resume.
 *
 * The webhook flips User.stripe_connect_charges_enabled when
 * Stripe confirms the account is ready. There can be a delivery
 * lag between the resident landing here and the webhook firing —
 * the GET /me/connect/status endpoint hits Stripe in real-time
 * and reconciles the cached flag, so this page reflects truth
 * even when the webhook is in transit. While Stripe is still
 * verifying (the card's "confirming" state) the status is polled
 * every 10 s so the page flips to ready on its own.
 *
 * With `?next=` (the earn walk sent the resident to Stripe) the
 * primary action returns to the walk's Get paid step instead of
 * the generic My Chargers list.
 *
 * Also the country chooser (item 2, 2026-10-02) for every entry point
 * that cannot ask: the charger page gate, the Earnings Stripe row, the
 * carbon card and the dashboard fallback send a host with no account
 * here. Before an account exists the page asks for the name as on the
 * ID and the bank account country (nothing preselected, confirmed by the
 * host), because the server creates no account without one. An existing
 * account resumes as before, its country shown read-only on the card.
 * Back returns to the screen that sent the host here (`?from=`).
 *
 * No account is created while the Api answering is one from before that
 * change (apiTakesCountry): it would ignore the country and make the
 * account Canadian for good. The page says so and re-reads the status
 * every 30 s until the new Api answers.
 */
export function ConnectCompletePage({ onNavigate }: Props) {
  const { t } = useTranslation('connect');
  const { t: tEarn } = useTranslation('earn');
  const user = useAuthStore((s) => s.user);
  const [next] = useState(readNext);
  const [from] = useState(readFrom);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['connect-status'],
    queryFn: fetchConnectStatus,
    // Refetch on window focus — if the resident bounced to Stripe
    // and back via a different tab, the underlying state changes
    // and we want this page to reflect that without a manual reload.
    refetchOnWindowFocus: true,
    // Poll while confirming, the one state that resolves without the
    // resident doing anything, and slowly while an old Api still answers
    // a host with no account (the deploy is minutes away from done).
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      if (connectCardState(d) === 'confirming') return 10_000;
      return !d.has_account && !apiTakesCountry(d) ? 30_000 : false;
    },
    refetchIntervalInBackground: false,
  });
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState('');
  // A new account only: the name Stripe verifies the ID against, and the
  // bank account country, which starts unpicked.
  const [legalName, setLegalName] = useState(user?.name ?? '');
  const [payout, setPayout] = useState<{ country: PayoutCountry | ''; confirmed: boolean }>({
    country: '', confirmed: false,
  });
  const hasAccount = data?.has_account === true;
  const takesCountry = apiTakesCountry(data);

  const handleResume = async (): Promise<void> => {
    setResuming(true);
    setResumeError('');
    const chosen = !hasAccount && payout.country ? payout.country : null;
    try {
      const name = legalName.trim();
      const result = await startNewPayoutAccount({
        // Carry `next` through so a second Stripe pass still lands back
        // on the walk.
        ...(next ? { next } : {}),
        // Creation only; the server ignores both for an existing account.
        ...(!hasAccount && name.length >= 2 ? { legalName: name.slice(0, 120) } : {}),
        ...(chosen ? { country: chosen } : {}),
      });
      if (result.kind === 'otherCountry' && chosen) {
        // The account came back in the other country (an old Api task
        // answered): stop before Stripe's checks, and show the account.
        const actual = payoutCountryFrom(result.country);
        void refetch();
        setResumeError(t('country.createdOther', {
          account: actual ? t(payoutCountryTextKey(actual)) : result.country,
          country: t(payoutCountryTextKey(chosen)),
        }));
        setResuming(false);
        return;
      }
      if (result.kind === 'url') window.location.href = result.url;
    } catch (err) {
      const { code, message } = await readErrorEnvelope(err);
      if (isCountryRequiredCode(code)) {
        // The status said an account exists but the server has none:
        // re-read it so the country choice shows.
        void refetch();
        setResumeError(t('country.required'));
      } else {
        setResumeError(message ?? (err instanceof Error ? err.message : t('error.startOnboarding')));
      }
      setResuming(false);
    }
  };

  const state = data ? connectCardState(data) : null;
  const mayResume = canStartPayout({
    hasAccount, country: payout.country, confirmed: payout.confirmed, apiTakesCountry: takesCountry,
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('header.back')}
          onClick={() => onNavigate(next ?? from ?? '/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('header.title')}</h1>
          <p className={styles.subtitle}>
            {isLoading ? t('header.checking') : data ? statusSubtitle(data, t) : ''}
          </p>
        </div>
      </header>

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('error.checkStatus')}
        </div>
      )}

      {!error && (
        <div className={styles.card}>
          <ConnectStatusCard status={data} loading={isLoading} />

          {state === 'ready' && (
            <p className={styles.cardBody}>{t('ready.body')}</p>
          )}

          {state === 'notStarted' && (
            <div className={styles.chooser}>
              <div className={formStyles.field}>
                <label htmlFor="connect-legal-name" className={formStyles.label}>
                  {tEarn('paid.legalName.label')}
                </label>
                <input
                  id="connect-legal-name"
                  className={formStyles.input}
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  placeholder={tEarn('paid.legalName.placeholder')}
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  disabled={resuming}
                />
              </div>
              <PayoutCountryField
                idPrefix="connect"
                country={payout.country}
                confirmed={payout.confirmed}
                onChange={setPayout}
                homeCountry={payoutCountryFrom(data?.home_country ?? null)}
                updating={!takesCountry}
                disabled={resuming}
              />
            </div>
          )}

          {resumeError && (
            <p className={styles.errorInline}>{resumeError}</p>
          )}

          {state !== null && (
            <div className={styles.actionRow}>
              {/* Back to the walk wins whenever it sent us here — the
                  walk re-reads the status itself and shows the same
                  card, so a still-confirming account is not a dead
                  end there either. */}
              {next ? (
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => onNavigate(`${next}?step=paid`)}
                >
                  {t('pending.continueEarning')}
                </button>
              ) : state === 'ready' ? (
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => onNavigate('/my-chargers')}
                >
                  {t('ready.continue')}
                </button>
              ) : null}

              {/* Needed / error / not started go back into Stripe's
                  form. Confirming is Stripe's turn — offer a manual
                  re-check instead of a form with nothing to fill. */}
              {state !== 'ready' && state !== 'confirming' && (
                <button
                  type="button"
                  className={next ? styles.secondaryBtn : styles.primaryBtn}
                  onClick={handleResume}
                  disabled={resuming || !mayResume}
                >
                  {resuming
                    ? <><Loader2 size={14} className={styles.spin} /> {t('pending.opening')}</>
                    : <><ExternalLink size={14} /> {state === 'notStarted' ? t('pending.startSetup') : t('pending.continueSetup')}</>}
                </button>
              )}
              {state !== 'ready' && (
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => refetch()}
                  disabled={resuming}
                >
                  {t('pending.checkStatus')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The `{ error: { code, message } }` envelope off a ky HTTPError; nulls
 *  when the error is not shaped like that (network drop). Same shape as
 *  the earn walk's helper, kept local so pages do not import each other. */
async function readErrorEnvelope(err: unknown): Promise<{ code: string | null; message: string | null }> {
  const response = (err as { response?: Response }).response;
  if (!response) return { code: null, message: null };
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    return { code: body.error?.code ?? null, message: body.error?.message ?? null };
  } catch {
    return { code: null, message: null };
  }
}

function statusSubtitle(
  data: ConnectStatus,
  t: (key: string) => string,
): string {
  // Same rule the card renders, so the header can never promise a
  // "details needed" the card then contradicts with "confirming".
  switch (connectCardState(data)) {
    case 'notStarted': return t('subtitle.notStarted');
    case 'ready': return t('subtitle.active');
    case 'confirming': return t('subtitle.confirming');
    case 'blocked': return t('subtitle.blocked');
    default: return t('subtitle.detailsNeeded');
  }
}
